// Reference lines for the boundary tools: admin-unit linework at one level,
// and roads — fetched for the view, drawn faintly, handed to the snapper.
//
// WHY THESE LINES. A control boundary almost never runs where a hand puts it.
// It runs along a district edge (control maps are drawn district by
// district), or along a road (a front holds a road), or along the coast. The
// editor already snapped to the other territories and to the country outline;
// this is the rest of what the ground is actually divided by.
//
//   * ADMIN LINEWORK comes from GET /api/boundaries/lines — the units of ONE
//     level, dissolved so a shared edge is one line, clipped to the view as
//     LINES (a polygon clipped at the view edge would grow a fake edge along
//     it, and a trace would happily follow that out to sea). It is the
//     detailed gbOpen set where a country's detail is loaded (migration 058)
//     and CGAZ otherwise — the server mixes them per country.
//   * ROADS come from GET /api/roads/lines — OpenStreetMap highways the
//     server fetched from Overpass once per cell and kept. Off by default:
//     the first look at a region costs an upstream call.
//
// Both are refetched on `moveend` only when the view has left the box the
// last answer covered (fetched with a margin), and both say out loud when
// the view is too wide for them rather than silently showing nothing.

import * as maplibregl from 'maplibre-gl';
import { get } from './api';
import type { Pt } from './snap';

export const SRC_ADM = 'terr-guide-adm';
export const SRC_ROAD = 'terr-guide-road';
export const L_ADM = 'terr-guide-adm-line';
export const L_ROAD = 'terr-guide-road-line';

/** Below these zooms a level's linework is too dense to read or to send. */
export const LEVEL_MINZOOM: Record<number, number> = { 1: 4, 2: 6, 3: 8, 4: 9, 5: 10 };
/** The server's per-level ceiling on a request's box (degrees²), mirrored so
 *  a wide view says "zoom in" instead of asking for a 422. */
export const LEVEL_MAX_DEG2: Record<number, number> = { 1: 400, 2: 120, 3: 30, 4: 8, 5: 3 };
export const ROADS_MINZOOM = 9;
/** The server caps a roads request at 36 z9 cells; ~3.5° square at the
 *  equator, less further north. Asking for less keeps it one Overpass call. */
export const ROADS_MAX_DEG2 = 6;

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

export interface LevelDto { level: number; source: string; units: number; detail: boolean }
export interface DetailLevelDto {
  level: number; status: 'loading' | 'ready' | 'error'; units: number | null;
  vertices: number | null; source: string | null; license: string | null;
  year: string | null; error: string | null; updatedAt: string | null;
}
export interface LevelsDto { iso: string; country: string; levels: LevelDto[]; detail: DetailLevelDto[] }

export interface GuideLine { pts: Pt[]; source: 'admin' | 'road' }

type Bbox = [number, number, number, number];

interface LinesDto extends GeoJSON.FeatureCollection {
  truncated?: boolean;
  error?: string | null;
}

/** The layers, pure, so a test can hold them to the style spec. Admin lines
 *  dashed in a colour no territory uses; roads solid amber, heavier for the
 *  bigger road. Both faint: they are guides, not layers the operator chose. */
export function guideLayers(): maplibregl.LayerSpecification[] {
  return [
    {
      id: L_ADM, type: 'line', source: SRC_ADM,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#c77dff', 'line-width': 1.3, 'line-dasharray': [3, 2],
               'line-opacity': 0.8 },
    },
    {
      id: L_ROAD, type: 'line', source: SRC_ROAD,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': '#f4a261',
        'line-width': ['match', ['get', 'rank'], 1, 2.4, 2, 2.1, 3, 1.8, 4, 1.4, 1.1],
        'line-opacity': 0.75,
      },
    },
  ];
}

export function installGuideLayers(map: maplibregl.Map): void {
  if (map.getSource(SRC_ADM)) return;
  map.addSource(SRC_ADM, { type: 'geojson', data: EMPTY });
  map.addSource(SRC_ROAD, { type: 'geojson', data: EMPTY });
  for (const layer of guideLayers()) map.addLayer(layer);
}

/** The lines of a FeatureCollection as open ground polylines. */
export function linesOf(fc: GeoJSON.FeatureCollection | null | undefined,
                        source: GuideLine['source']): GuideLine[] {
  const out: GuideLine[] = [];
  for (const f of fc?.features ?? []) {
    const g = f.geometry;
    const parts = g?.type === 'LineString' ? [g.coordinates]
      : g?.type === 'MultiLineString' ? g.coordinates : [];
    for (const coords of parts) {
      const pts = coords.map((c): Pt => [c[0]!, c[1]!]);
      if (pts.length >= 2) out.push({ pts, source });
    }
  }
  return out;
}

/** The view's box with a margin, so a small pan needs no refetch. */
export function paddedBounds(b: Bbox, pad = 0.25): Bbox {
  const pw = (b[2] - b[0]) * pad;
  const ph = (b[3] - b[1]) * pad;
  return [Math.max(-180, b[0] - pw), Math.max(-85, b[1] - ph),
          Math.min(180, b[2] + pw), Math.min(85, b[3] + ph)];
}

export const bboxArea = (b: Bbox): number => (b[2] - b[0]) * (b[3] - b[1]);
export const bboxWithin = (inner: Bbox, outer: Bbox): boolean =>
  inner[0] >= outer[0] && inner[1] >= outer[1] && inner[2] <= outer[2] && inner[3] <= outer[3];

/** Which admin level comes next on the bar's cycle button: off, then each
 *  available level coarse to fine, then off again. */
export function nextLevel(current: number, available: number[]): number {
  const levels = [...new Set(available)].sort((a, b) => a - b);
  if (!levels.length) return 0;
  if (!current) return levels[0]!;
  const i = levels.indexOf(current);
  return i < 0 || i === levels.length - 1 ? 0 : levels[i + 1]!;
}

/** Road classes worth drawing at a zoom: motorways to secondaries while a
 *  view is still regional, tertiaries from z10, unclassified from z12. */
export function roadRankFor(zoom: number): number {
  return zoom >= 12 ? 6 : zoom >= 10 ? 5 : 4;
}

export interface GuideSet {
  /** The admin level drawn and snapped to; 0 = off. */
  level(): number;
  roads(): boolean;
  /** The country's effective levels (detailed where loaded, else CGAZ). */
  levels(): LevelDto[];
  cycleLevel(): number;
  /** Switch to a level the country has; returns the level now in force. */
  setLevel(level: number): number;
  setRoads(on: boolean): void;
  /** What is loaded for the view now. */
  lines(): GuideLine[];
  /** The bar button's label for the admin cycle. */
  levelLabel(): string;
  /** A few words on what the guides are doing — shown when it matters. */
  status(): string;
  destroy(): void;
}

export interface GuideOpts {
  /** The country whose levels the cycle offers. Empty: the one under the
   *  map's centre. */
  iso: string;
  /** Start with this admin level on when the country has it (default 2). */
  level?: number;
  /** New lines arrived (or were dropped): re-index the snapper. */
  onChange: () => void;
}

/** One set of guides for one editing session. Only one session edits at a
 *  time, so the sources are shared and `destroy` empties them. */
export function createGuideSet(map: maplibregl.Map, opts: GuideOpts): GuideSet {
  installGuideLayers(map);
  let level = 0;
  let roadsOn = false;
  let levels: LevelDto[] = [];
  let adm: GuideLine[] = [];
  let road: GuideLine[] = [];
  let admKey = '';
  let roadKey = '';
  let admBox: Bbox | null = null;
  let roadBox: Bbox | null = null;
  let admNote = '';
  let roadNote = '';
  let seq = 0;
  let timer: number | null = null;
  let dead = false;

  const view = (): Bbox => {
    const b = map.getBounds();
    return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
  };
  const setSource = (id: string, fc: GeoJSON.FeatureCollection): void => {
    (map.getSource(id) as maplibregl.GeoJSONSource | undefined)?.setData(fc);
  };

  async function refreshAdmin(): Promise<void> {
    const my = ++seq;
    if (!level) {
      adm = []; admBox = null; admKey = ''; admNote = '';
      setSource(SRC_ADM, EMPTY);
      return;
    }
    const v = view();
    const zoom = map.getZoom();
    if (zoom < (LEVEL_MINZOOM[level] ?? 8)) {
      adm = []; admBox = null; admKey = '';
      admNote = `ADM${level}: zoom in`;
      setSource(SRC_ADM, EMPTY);
      opts.onChange();
      return;
    }
    const box = paddedBounds(v);
    const key = `${level}`;
    if (admBox && admKey === key && bboxWithin(v, admBox)) return;
    const want = bboxArea(box) > (LEVEL_MAX_DEG2[level] ?? 30) ? v : box;
    if (bboxArea(want) > (LEVEL_MAX_DEG2[level] ?? 30)) {
      adm = []; admBox = null;
      admNote = `ADM${level}: zoom in`;
      setSource(SRC_ADM, EMPTY);
      opts.onChange();
      return;
    }
    admNote = `ADM${level} loading…`;
    try {
      const [w, s, e, n] = want;
      const fc = await get<LinesDto>(
        `/api/boundaries/lines?w=${w.toFixed(5)}&s=${s.toFixed(5)}&e=${e.toFixed(5)}&n=${n.toFixed(5)}&level=${level}`);
      if (dead || my !== seq) return;
      adm = linesOf(fc, 'admin');
      admBox = want;
      admKey = key;
      admNote = fc.truncated ? `ADM${level}: too much in view, zoom in` : '';
      setSource(SRC_ADM, fc);
    } catch (err) {
      if (dead) return;
      adm = []; admBox = null;
      admNote = `ADM${level} unavailable`;
      setSource(SRC_ADM, EMPTY);
      console.warn('admin guide lines', err);
    }
    opts.onChange();
  }

  let roadSeq = 0;
  async function refreshRoads(): Promise<void> {
    const my = ++roadSeq;
    if (!roadsOn) {
      road = []; roadBox = null; roadKey = ''; roadNote = '';
      setSource(SRC_ROAD, EMPTY);
      return;
    }
    const v = view();
    const zoom = map.getZoom();
    if (zoom < ROADS_MINZOOM || bboxArea(v) > ROADS_MAX_DEG2) {
      road = []; roadBox = null; roadKey = '';
      roadNote = 'roads: zoom in';
      setSource(SRC_ROAD, EMPTY);
      opts.onChange();
      return;
    }
    const rank = roadRankFor(zoom);
    const key = `${rank}`;
    if (roadBox && roadKey === key && bboxWithin(v, roadBox)) return;
    const box = paddedBounds(v, 0.15);
    const want = bboxArea(box) > ROADS_MAX_DEG2 ? v : box;
    roadNote = 'roads loading…';
    opts.onChange();
    try {
      const [w, s, e, n] = want;
      const fc = await get<LinesDto>(
        `/api/roads/lines?w=${w.toFixed(5)}&s=${s.toFixed(5)}&e=${e.toFixed(5)}&n=${n.toFixed(5)}&maxRank=${rank}`);
      if (dead || my !== roadSeq) return;
      road = linesOf(fc, 'road');
      roadBox = want;
      roadKey = key;
      roadNote = fc.error ? `roads: ${fc.error}` : '';
      setSource(SRC_ROAD, fc);
    } catch (err) {
      if (dead) return;
      road = []; roadBox = null;
      roadNote = err instanceof Error ? `roads: ${err.message}` : 'roads unavailable';
      setSource(SRC_ROAD, EMPTY);
    }
    opts.onChange();
  }

  const onMoveEnd = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = null;
      void refreshAdmin();
      void refreshRoads();
    }, 250);
  };
  map.on('moveend', onMoveEnd);
  if (map.getLayer(L_ADM)) map.setLayoutProperty(L_ADM, 'visibility', 'visible');
  if (map.getLayer(L_ROAD)) map.setLayoutProperty(L_ROAD, 'visibility', 'visible');

  void (async () => {
    const c = map.getCenter();
    const where = opts.iso ? `iso=${encodeURIComponent(opts.iso)}`
      : `lon=${c.lng.toFixed(5)}&lat=${c.lat.toFixed(5)}`;
    try {
      levels = (await get<LevelsDto>(`/api/boundaries/levels?${where}`)).levels;
    } catch {
      levels = [{ level: 1, source: 'CGAZ', units: 0, detail: false },
                { level: 2, source: 'CGAZ', units: 0, detail: false }];
    }
    if (dead) return;
    const want = opts.level ?? 2;
    level = levels.some((l) => l.level === want) ? want
      : levels.length ? levels[Math.min(levels.length - 1, 1)]!.level : 0;
    await refreshAdmin();
    opts.onChange();
  })();

  return {
    level: () => level,
    roads: () => roadsOn,
    levels: () => levels,
    cycleLevel() {
      level = nextLevel(level, levels.map((l) => l.level));
      admBox = null;
      void refreshAdmin();
      opts.onChange();
      return level;
    },
    setLevel(want) {
      if (want === level || !levels.some((l) => l.level === want)) return level;
      level = want;
      admBox = null;
      void refreshAdmin();
      opts.onChange();
      return level;
    },
    setRoads(on) {
      roadsOn = on;
      roadBox = null;
      void refreshRoads();
      opts.onChange();
    },
    lines: () => [...adm, ...road],
    levelLabel() {
      if (!level) return '▦ Admin off';
      const l = levels.find((x) => x.level === level);
      return `▦ ADM${level}${l?.detail ? ' ·detail' : ''}`;
    },
    status() {
      const parts = [admNote, roadNote].filter(Boolean);
      if (roadsOn && !roadNote) parts.push('roads © OpenStreetMap');
      return parts.join(' · ');
    },
    destroy() {
      dead = true;
      map.off('moveend', onMoveEnd);
      if (timer !== null) window.clearTimeout(timer);
      setSource(SRC_ADM, EMPTY);
      setSource(SRC_ROAD, EMPTY);
    },
  };
}
